<template>
	<div class="user-center-container enterprise-company-page">
		<div class="user-center-content">
			<tab-pane v-model="activeTab" :tabs="tabs" :pane-style="tabPaneStyle">
				<section v-show="activeTab === 'info'" class="tab-content">
					<div class="company-info-card">
						<div class="company-emblem"><i class="el-icon-office-building"></i></div>
						<div class="company-details">
							<div class="company-title-row">
								<h2>{{ companyInfo.companyName }}</h2>
								<span class="verified-badge">
									<i class="el-icon-success"></i>
									企业已认证
								</span>
							</div>
							<div class="company-code">
								<span>统一社会信用代码</span>
								<strong>{{ companyInfo.companyCode }}</strong>
							</div>
							<p class="verified-tip">企业账号通过统一身份认证登录，登录后默认完成企业认证。</p>
						</div>
						<div class="verified-seal">
							<i class="el-icon-medal"></i>
							<span>已认证</span>
						</div>
					</div>

					<company-auth-rules></company-auth-rules>
					<company-faq></company-faq>
				</section>

				<section v-show="activeTab === 'authorization'" class="tab-content authorization-content">
					<div class="process-card">
						<div class="block-heading">
							<h3>授权流程说明</h3>
							<p>完成以下三个步骤，即可为个人用户授予企业认证身份</p>
						</div>
						<div class="process-steps">
							<div v-for="(step, index) in processSteps" :key="step.key" class="process-step">
								<div class="step-icon"><i :class="step.icon"></i></div>
								<div class="step-copy">
									<strong>{{ step.title }}</strong>
									<p>{{ step.description }}</p>
								</div>
								<div v-if="index < processSteps.length - 1" class="process-line">
									<span></span>
								</div>
							</div>
						</div>
					</div>

					<div class="authorize-card">
						<div class="block-heading">
							<h3>授权个人用户</h3>
							<p>输入用户完整手机号或身份证号完成授权，最多可同时授权 {{ authorizationLimit }} 人</p>
						</div>
						<div class="authorize-form">
							<el-input
								v-model.trim="authorizeKeyword"
								clearable
								maxlength="18"
								placeholder="请输入用户手机号或身份证号"
								@keyup.enter.native="handleAuthorize"
							>
								<i slot="prefix" class="el-input__icon el-icon-user"></i>
							</el-input>
							<el-button type="primary" icon="el-icon-s-check" :loading="authorizeLoading" @click="handleAuthorize">
								授权
							</el-button>
						</div>
					</div>

					<div class="user-list-card">
						<div class="list-heading">
							<div>
								<h3>授权用户列表</h3>
								<p>共 {{ pageOptions.total }} 条授权记录</p>
							</div>
							<div class="status-legend">
								<i></i>
								最多可同时授权 {{ authorizationLimit }} 人
							</div>
						</div>
						<el-table
							v-loading="listLoading"
							:data="authorizationList"
							border
							stripe
							empty-text="暂无授权记录"
							class="authorization-table"
						>
							<el-table-column prop="personalRealName" label="昵称/姓名"></el-table-column>
							<el-table-column prop="personalUserName" label="身份证号"></el-table-column>
							<el-table-column prop="personalPhone" label="手机号" min-width="120"></el-table-column>
							<el-table-column prop="authorizeTime" label="授权日期" width="110">
								<template slot-scope="scope">{{ formatRecordDate(scope.row.authorizeTime) }}</template>
							</el-table-column>
							<el-table-column prop="expireTime" label="截止日期" width="110">
								<template slot-scope="scope">{{ formatRecordDate(scope.row.expireTime) }}</template>
							</el-table-column>
							<el-table-column prop="unbindTime" label="解绑日期" width="110">
								<template slot-scope="scope">{{ formatRecordDate(scope.row.unbindTime) }}</template>
							</el-table-column>
							<el-table-column prop="unbindByName" label="解绑人" min-width="100">
								<template slot-scope="scope">{{ scope.row.unbindByName || '--' }}</template>
							</el-table-column>
							<el-table-column label="状态" width="90" align="center">
								<template slot-scope="scope">
									<span :class="['status-tag', getStatusClass(scope.row.status)]">
										{{ getStatusText(scope.row.status) }}
									</span>
								</template>
							</el-table-column>
							<el-table-column label="操作" width="90" align="center">
								<template slot-scope="scope">
									<el-button
										v-if="Number(scope.row.status) === 1"
										type="danger"
										plain
										size="mini"
										:loading="unbindingAuthorizationId === scope.row.authorizationId"
										@click="handleUnbind(scope.row)"
									>
										解绑
									</el-button>
									<span v-else class="disabled-action">--</span>
								</template>
							</el-table-column>
						</el-table>
						<div v-if="pageOptions.total > pageOptions.limit" class="authorization-pagination">
							<el-pagination
								background
								layout="total, sizes, prev, pager, next"
								:total="pageOptions.total"
								:current-page.sync="pageOptions.page"
								:page-size.sync="pageOptions.limit"
								:page-sizes="[10, 20, 50]"
								@size-change="handlePageSizeChange"
								@current-change="handlePageChange"
							></el-pagination>
						</div>
					</div>
				</section>
			</tab-pane>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex'
	import TabPane from '@/components/TabPane'
	import CompanyAuthRules from './components/AuthRules'
	import CompanyFaq from './components/CompanyFaq'
	import { parseTime } from '@/utils'
	import { isIdCard, isMobile } from '@/utils/validate'
	import {
		bindAuthorizedPersonalUser,
		getAuthorizedUserList,
		queryAuthorizedPersonalUser,
		unbindAuthorizedUser
	} from '@/api/userCenter/companyManagement'

	export default {
		name: 'EnterpriseCompany',
		components: { TabPane, CompanyAuthRules, CompanyFaq },
		data() {
			return {
				activeTab: 'info',
				tabs: [
					{ name: '企业认证信息', value: 'info' },
					{ name: '授权管理', value: 'authorization' }
				],
				tabPaneStyle: {
					'--tab-pane-header-padding-bottom': '5px',
					'--tab-pane-header-margin-bottom': '18px',
					'--tab-pane-item-font-size': '16px',
					'--tab-pane-indicator-bottom': '-5px'
				},
				processSteps: [
					{ key: 'register', title: '个人用户注册登录', description: '用户完成楚建通个人账号注册', icon: 'el-icon-user' },
					{ key: 'authorize', title: '企业发起授权', description: '企业输入手机号或身份证号授权', icon: 'el-icon-s-check' },
					{ key: 'complete', title: '授权完成', description: '用户自动获得企业认证身份', icon: 'el-icon-circle-check' }
				],
				authorizeKeyword: '',
				// 企业最多可同时授权的人数由页面统一配置；是否超过上限最终由绑定接口校验。
				authorizationLimit: 5,
				authorizeLoading: false,
				listLoading: false,
				authorizationList: [],
				pageOptions: {
					page: 1,
					limit: 10,
					total: 0
				},
				unbindingAuthorizationId: ''
			}
		},
		computed: {
			...mapGetters(['userInfo']),
			companyInfo() {
				return {
					companyName: this.userInfo.companyName,
					companyCode: this.userInfo.companyCode
				}
			}
		},
		created() {
			this.loadAuthorizationList()
		},
		methods: {
			// 授权记录默认查询全部状态，status 始终传空，不附加前端状态筛选条件。
			async loadAuthorizationList() {
				this.listLoading = true
				try {
					const response = await getAuthorizedUserList({
						page: this.pageOptions.page,
						limit: this.pageOptions.limit,
						status: ''
					})
					const pageData = response.data || {}
					this.authorizationList = pageData.records || []
					this.pageOptions.total = Number(pageData.total || 0)
				} finally {
					this.listLoading = false
				}
			},
			getStatusText(status) {
				return { 1: '已授权', 2: '已解绑', 3: '已过期' }[Number(status)] || '未知'
			},
			getStatusClass(status) {
				if (Number(status) === 1) return 'is-authorized'
				if (Number(status) === 3) return 'is-expired'
				return 'is-unbound'
			},
			// 授权记录只展示年月日，空日期统一使用占位符。
			formatRecordDate(value) {
				return parseTime(value, '{y}-{m}-{d}') || '--'
			},
			// 点击授权后先静默查询个人用户；仅当用户存在且允许绑定时，直接调用绑定接口。
			async handleAuthorize() {
				const keyword = this.authorizeKeyword
				if (!isMobile(keyword) && !isIdCard(keyword)) {
					this.$message.warning('请输入正确的手机号或身份证号')
					return
				}
				this.authorizeLoading = true
				try {
					const response = await queryAuthorizedPersonalUser({ keyword })
					const userList = response.data || []
					const user = userList[0]
					if (!user) {
						this.$message.warning('未查询到符合条件的个人用户')
						return
					}
					if (!this.getBindableValue(user.bindable)) {
						this.$message.warning(user.unavailableReason || '当前用户暂不可授权')
						return
					}
					await bindAuthorizedPersonalUser({ personalUserId: user.personalUserId })
					this.$message.success('授权成功')
					this.authorizeKeyword = ''
					this.pageOptions.page = 1
					this.loadAuthorizationList()
				} finally {
					this.authorizeLoading = false
				}
			},
			// 仅当 bindable 为 true 时，才允许绑定。
			getBindableValue(bindable) {
				return bindable === true
			},
			handleUnbind(row) {
				this.$modal
					.confirm(`确认解除与“${row.personalRealName || row.personalUserName || '--'}”的企业授权关系吗？`)
					.then(async () => {
						this.unbindingAuthorizationId = row.authorizationId
						try {
							await unbindAuthorizedUser({ authorizationId: row.authorizationId })
							this.$message.success('解绑成功')
							this.loadAuthorizationList()
						} finally {
							this.unbindingAuthorizationId = ''
						}
					})
					.catch(() => {})
			},
			handlePageSizeChange(limit) {
				this.pageOptions.page = 1
				this.pageOptions.limit = limit
				this.loadAuthorizationList()
			},
			handlePageChange(page) {
				this.pageOptions.page = page
				this.loadAuthorizationList()
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import '~@/views/user/styles/user-center.scss';

	.tab-content {
		padding-bottom: 4px;
	}

	.company-info-card {
		position: relative;
		display: flex;
		align-items: center;
		min-height: 150px;
		padding: 26px 30px;
		overflow: hidden;
		border: 1px solid #acd2bc;
		border-radius: 10px;
		background: linear-gradient(135deg, #f0fbf5 0%, #ffffff 68%);

		&::after {
			position: absolute;
			top: -60px;
			right: -35px;
			width: 190px;
			height: 190px;
			border-radius: 50%;
			background: rgba(39, 166, 105, 0.07);
			content: '';
		}
	}

	.company-emblem {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 74px;
		height: 74px;
		margin-right: 22px;
		border-radius: 16px;
		color: #ffffff;
		font-size: 37px;
		background: linear-gradient(145deg, #2fb477, #188957);
		box-shadow: 0 8px 18px rgba(31, 153, 94, 0.2);
	}

	.company-details {
		position: relative;
		z-index: 1;
		min-width: 0;
	}

	.company-title-row {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 12px;

		h2 {
			margin: 0;
			color: #263547;
			font-size: 22px;
		}
	}

	.verified-badge,
	.status-tag {
		display: inline-flex;
		align-items: center;
		padding: 4px 10px;
		border-radius: 13px;
		font-size: 13px;
	}

	.verified-badge {
		color: #188957;
		background: #dff5e9;

		i {
			margin-right: 5px;
		}
	}

	.company-code {
		margin-top: 16px;
		color: #748090;

		span {
			margin-right: 16px;
		}

		strong {
			color: #424f61;
			letter-spacing: 0.5px;
		}
	}

	.verified-tip {
		margin: 10px 0 0;
		color: #839086;
		font-size: 13px;
	}

	.verified-seal {
		position: relative;
		z-index: 1;
		display: flex;
		align-items: center;
		margin-left: auto;
		padding: 10px 14px;
		border: 1px solid #9fd2b5;
		border-radius: 6px;
		color: #188957;
		background: rgba(255, 255, 255, 0.75);

		i {
			margin-right: 7px;
			font-size: 20px;
		}
	}

	.process-card,
	.authorize-card,
	.user-list-card {
		padding: 22px;
		border: 1px solid #e5eaf1;
		border-radius: 8px;
		background: #ffffff;
	}

	.authorize-card,
	.user-list-card {
		margin-top: 20px;
	}

	.block-heading,
	.list-heading {
		h3 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 18px;
		}

		p {
			margin: 0;
			color: #9098a5;
			font-size: 13px;
		}
	}

	.process-steps {
		display: flex;
		align-items: center;
		margin-top: 24px;
		padding: 24px;
		border-radius: 8px;
		background: linear-gradient(135deg, #f6faff, #fbfdff);
	}

	.process-step {
		position: relative;
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 0;
		min-height: 96px;
		padding: 18px;
		border: 1px solid #dae7f6;
		border-radius: 10px;
		background: #ffffff;
		box-shadow: 0 4px 12px rgba(51, 101, 159, 0.05);
		transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;

		&:not(:last-child) {
			margin-right: 68px;
		}

		&:hover {
			border-color: #8ebbea;
			box-shadow: 0 9px 22px rgba(38, 113, 203, 0.13);
			transform: translateY(-4px);

			.step-icon {
				color: #ffffff;
				background: #2671cb;
				transform: scale(1.08) rotate(-4deg);
			}
		}
	}

	.step-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 48px;
		height: 48px;
		margin-right: 12px;
		border-radius: 50%;
		color: #2671cb;
		font-size: 23px;
		background: #e7f1ff;
		transition: color 0.25s ease, background-color 0.25s ease, transform 0.25s ease;
	}

	.step-copy {
		min-width: 0;

		strong,
		p {
			display: block;
		}

		strong {
			margin-bottom: 6px;
			color: #344155;
			font-size: 16px;
		}

		p {
			margin: 0;
			color: #8b94a2;
			font-size: 12px;
			line-height: 18px;
		}
	}

	.process-line {
		position: absolute;
		top: 50%;
		left: calc(100% + 8px);
		width: 52px;
		height: 2px;
		background: linear-gradient(90deg, #bdd5ef, #6da5df);
		transform: translateY(-50%);

		&::after {
			position: absolute;
			top: 50%;
			right: -1px;
			width: 8px;
			height: 8px;
			border-top: 2px solid #6da5df;
			border-right: 2px solid #6da5df;
			content: '';
			transform: translateY(-50%) rotate(45deg);
		}

		span {
			position: absolute;
			top: 50%;
			left: 0;
			width: 7px;
			height: 7px;
			border: 2px solid #ffffff;
			border-radius: 50%;
			background: #2671cb;
			box-shadow: 0 0 0 2px rgba(38, 113, 203, 0.14);
			transform: translateY(-50%);
			animation: process-dot 2.2s ease-in-out infinite;
		}
	}

	.authorize-form {
		display: flex;
		width: 100%;
		margin-top: 20px;

		.el-input {
			margin-right: 12px;
		}

		.el-button {
			flex-shrink: 0;
		}
	}

	.list-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 18px;
	}

	.status-legend {
		color: #697586;
		font-size: 14px;

		i {
			display: inline-block;
			width: 8px;
			height: 8px;
			margin-right: 7px;
			border-radius: 50%;
			background: #22a06b;
		}
	}

	.authorization-table {
		width: 100%;

		::v-deep th {
			color: #435066;
			background: #f4f7fb;
		}
	}

	.status-tag {
		padding: 3px 9px;

		&.is-authorized {
			color: #168653;
			background: #e3f6ec;
		}

		&.is-unbound {
			color: #838c99;
			background: #eef0f3;
		}

		&.is-expired {
			color: #a56e28;
			background: #fff2dc;
		}
	}

	.authorization-pagination {
		padding-top: 18px;
		text-align: right;
	}

	.disabled-action {
		color: #b5bbc4;
	}

	@keyframes process-dot {
		0%,
		100% {
			left: 0;
		}

		50% {
			left: calc(100% - 7px);
		}
	}

	@media (max-width: 1100px) {
		.process-steps {
			align-items: stretch;
			flex-direction: column;
		}

		.process-line {
			top: calc(100% + 8px);
			left: 50%;
			width: 2px;
			height: 36px;
			background: linear-gradient(180deg, #bdd5ef, #6da5df);
			transform: translateX(-50%);

			&::after {
				top: auto;
				right: 50%;
				bottom: -1px;
				transform: translateX(50%) rotate(135deg);
			}

			span {
				display: none;
			}
		}

		.process-step:not(:last-child) {
			margin-right: 0;
			margin-bottom: 52px;
		}

		.verified-seal {
			display: none;
		}
	}
</style>
